// Signup Page - Parts4Drive (Unified Better Auth Architecture)
'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { Mail, Lock, Eye, EyeOff, Loader2, User, Home, ChevronRight, Store } from 'lucide-react'
import { authClient } from '@/lib/auth'
import { getOnboardingService } from '@/lib/onboarding'
import BrandLogo from '@/components/BrandLogo'
import { VerifyEmailDialog } from '@/components/VerifyEmailDialog'

function SignupForm() {
  const router = useRouter()

  const [accountType, setAccountType] = useState<'buyer' | 'merchant'>('buyer')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showVerify, setShowVerify] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState('')
  const [inviteInfo, setInviteInfo] = useState<{ orgId: string; orgName: string; role: string } | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const roleParam = (searchParams.get('role') || searchParams.get('type') || '').toLowerCase()
      if (roleParam === 'merchant' || roleParam === 'seller' || roleParam === 'vendor' || roleParam === 'dealer') {
        setAccountType('merchant')
      }

      const invite = searchParams.get('invite')
      if (invite) {
        const info = {
          orgId: invite,
          orgName: searchParams.get('orgName') || 'Auto Parts Store',
          role: searchParams.get('role') || 'CASHIER',
        }
        localStorage.setItem('p4d_pending_invite', JSON.stringify(info))
        setInviteInfo(info)
      }
    } catch {}
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    })
    setError(null)
  }

  const handleGoogleSignUp = () => {
    if (!acceptedTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy before continuing with Google.')
      return
    }
    const targetRole = accountType === 'merchant' ? 'vendor' : 'buyer'
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('p4d_intended_role', targetRole)
    }
    const onboarding = getOnboardingService()
    onboarding.orgData = { provider: 'google', role: targetRole }
    authClient.signInGoogle('/onboarding')
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!acceptedTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to create an account.')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setLoading(true)

    try {
      const res = await authClient.signUpEmail({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      })

      if (!res.success) {
        const msg = (res.error || '').toLowerCase()
        if (msg.includes('already') || msg.includes('exists') || res.status === 409) {
          setError('An account with this email already exists. Redirecting to sign in...')
          setTimeout(() => {
            router.push(`/login?email=${encodeURIComponent(formData.email)}`)
          }, 1500)
          return
        }
        setError(res.error || 'Failed to create account. Please try again.')
        setLoading(false)
        return
      }

      const targetRole = accountType === 'merchant' ? 'vendor' : 'buyer'
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('p4d_intended_role', targetRole)
      }
      const onboarding = getOnboardingService()
      onboarding.orgData = {
        email: formData.email,
        displayName: formData.name,
        provider: 'email',
        role: targetRole,
      }

      setVerifyEmail(formData.email)
      setShowVerify(true)
    } catch (err: any) {
      setError(err?.message || 'Failed to create account.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifySuccess = (data: any) => {
    setShowVerify(false)
    const token = data?.token || data?.session?.token || authClient.getToken()
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : ''
    const isProd = typeof window !== 'undefined' && window.location.hostname.includes('parts4drive.com')

    if (isProd) {
      window.location.href = `https://parts4drive.com/onboarding${tokenParam}`
    } else {
      router.push(`/onboarding${tokenParam}`)
    }
  }

  return (
    <>
      <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-50 text-slate-800" data-theme="light">
        <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-200/80 max-h-[92vh] overflow-y-auto my-auto shrink-0">
          {/* Breadcrumb Navigation back to Home */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 mb-5 font-medium">
            <Link href="/" className="flex items-center gap-1 hover:text-[#0A1B4A] transition-colors">
              <Home className="h-3.5 w-3.5" />
              <span>Home</span>
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-400" />
            <span className="text-[#0A1B4A] font-semibold">Sign Up</span>
          </nav>

          <div className="flex flex-col items-center text-center mb-6">
            <Link
              href="/"
              className="size-16 rounded-2xl bg-[#0A1B4A]/5 p-2 flex items-center justify-center border border-slate-200/80 shadow-xs mb-3 hover:scale-105 transition-transform overflow-hidden cursor-pointer"
              title="Go to Homepage"
            >
              <BrandLogo type="icon" theme="light" className="size-full object-contain" />
            </Link>
            <h1 className="font-display text-2xl font-bold text-[#0A1B4A]">Create your account</h1>
            <p className="text-slate-500 text-xs font-medium mt-1">
              Join Parts4Drive Inventory — Automotive stock control &amp; Marketplace
            </p>
            {inviteInfo && (
              <div className="mt-3 w-full rounded-2xl bg-blue-50 border border-blue-200/80 p-3 text-xs text-slate-700 flex items-center gap-2.5 text-left">
                <span className="size-2 rounded-full bg-blue-600 animate-pulse shrink-0" />
                <div className="min-w-0">
                  You were invited to join <span className="font-bold text-[#0A1B4A]">{inviteInfo.orgName}</span> as{' '}
                  <span className="font-bold text-[#2563EB]">{inviteInfo.role}</span>. Complete signup below to join the team workspace.
                </div>
              </div>
            )}
          </div>

          {/* Account Type Selector / Become a Merchant Button */}
          <div className="space-y-2 mb-4">
            <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 rounded-2xl border border-slate-200">
              <button
                type="button"
                onClick={() => setAccountType('buyer')}
                className={`py-2 px-3 rounded-xl text-xs font-bold transition-all text-center ${
                  accountType === 'buyer'
                    ? 'bg-white text-[#0A1B4A] shadow-xs border border-slate-200/60'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Customer / Buyer
              </button>
              <button
                type="button"
                onClick={() => setAccountType('merchant')}
                className={`py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 text-center ${
                  accountType === 'merchant'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30 ring-2 ring-emerald-500/20'
                    : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/80'
                }`}
              >
                <Store className="size-3.5" />
                <span>Become a Merchant</span>
              </button>
            </div>

            {accountType === 'merchant' ? (
              <div className="rounded-2xl bg-emerald-50/90 border border-emerald-300 p-3 text-xs text-emerald-950 flex items-start gap-2.5 shadow-xs">
                <Store className="size-4 text-emerald-700 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-black text-emerald-900 text-xs">Registering as Verified Merchant / Auto Parts Dealer</div>
                  <div className="text-[11px] text-emerald-800 leading-tight mt-0.5 font-medium">
                    List auto parts in Ladipo, ASPAMDA, Abossey Okai, Suame &amp; Nairobi. Direct WhatsApp &amp; POS stock sync included.
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            {/* Google Sign Up */}
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold h-11"
              onClick={handleGoogleSignUp}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>

            <div className="relative flex items-center gap-3">
              <Separator className="flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium shrink-0">or continue with email</span>
              <Separator className="flex-1 bg-slate-200" />
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs font-semibold text-slate-700">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    className="pl-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#0A1B4A]/20 focus:border-[#0A1B4A]"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    className="pl-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#0A1B4A]/20 focus:border-[#0A1B4A]"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    className="pl-10 pr-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#0A1B4A]/20 focus:border-[#0A1B4A]"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="text-xs font-semibold text-slate-700">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 rounded-full border border-slate-200 bg-white text-slate-800 h-11 focus:ring-2 focus:ring-[#0A1B4A]/20 focus:border-[#0A1B4A]"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="flex items-start space-x-2.5 pt-1">
                <Checkbox
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => {
                    setAcceptedTerms(!!checked)
                    if (checked) setError(null)
                  }}
                  id="terms"
                />
                <label
                  htmlFor="terms"
                  className="text-xs text-slate-600 leading-relaxed cursor-pointer select-none"
                >
                  I agree to the{' '}
                  <Link href="/compliance" className="text-[#0A1B4A] hover:underline font-semibold">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/security" className="text-[#0A1B4A] hover:underline font-semibold">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {error && (
                <div className="text-xs font-medium text-rose-600 bg-rose-50 p-3 rounded-xl border border-rose-200">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full rounded-full bg-[#0A1B4A] hover:bg-[#0f2560] text-white font-bold h-11 shadow-sm disabled:opacity-50"
                disabled={loading || !acceptedTerms}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account'}
              </Button>
            </form>

            <div className="text-center text-xs pt-2">
              <span className="text-slate-500">Already have an account? </span>
              <Link href="/login" className="text-[#0A1B4A] hover:underline font-bold">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>

      <VerifyEmailDialog
        open={showVerify}
        onOpenChange={setShowVerify}
        email={verifyEmail}
        purpose="signup"
        onSuccess={handleVerifySuccess}
      />
    </>
  )
}

export default function SignupPage() {
  return <SignupForm />
}
